import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Card, Badge, EmptyState } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const SESSION_TYPES = ["1-on-1 Coaching", "Group Training", "SOP Walkthrough", "Assessment", "Follow-up Check-in"];

const emptyForm = () => ({
  date: new Date().toISOString().slice(0, 10),
  department_id: "",
  member_ids: [],
  session_type: "1-on-1 Coaching",
  criteria_ids: [],
  duration: 30,
  conducted_by: "Trainer",
  outcome_notes: "",
  follow_up_required: false,
  follow_up_date: "",
});

export default function Sessions() {
  const { departments, deptName, selectedDept } = useApp();
  const [sessions, setSessions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);
  const [members, setMembers] = useState([]);
  const [criteria, setCriteria] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");

  const load = useCallback(() => {
    api.get("/sessions", {
      params: {
        department_id: selectedDept !== "all" ? selectedDept : undefined,
        session_type: typeFilter !== "all" ? typeFilter : undefined,
      },
    }).then((r) => setSessions(r.data)).catch(() => {});
  }, [selectedDept, typeFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!form.department_id) { setMembers([]); setCriteria([]); return; }
    api.get("/team-members", { params: { department_id: form.department_id } }).then((r) => setMembers(r.data));
    api.get("/criteria", { params: { department_id: form.department_id } }).then((r) => setCriteria(r.data.filter((c) => c.active)));
  }, [form.department_id]);

  const openNew = () => { setForm(emptyForm()); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...emptyForm(), ...s, follow_up_date: s.follow_up_date || "" }); setEditId(s.id); setOpen(true); };

  const save = async () => {
    if (!form.department_id || form.member_ids.length === 0) {
      toast.error("Select a department and at least one member.");
      return;
    }
    const payload = { ...form, duration: Number(form.duration), follow_up_date: form.follow_up_date || null };
    try {
      if (editId) await api.put(`/sessions/${editId}`, payload);
      else await api.post("/sessions", payload);
      toast.success(editId ? "Session updated." : "Session logged.");
      setOpen(false); load();
    } catch (e) { toast.error("Failed to save session."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this session log?")) return;
    await api.delete(`/sessions/${id}`);
    toast.success("Session deleted."); load();
  };

  const toggleMember = (id) => {
    setForm((f) => ({ ...f, member_ids: f.member_ids.includes(id) ? f.member_ids.filter((m) => m !== id) : [...f.member_ids, id] }));
  };
  const toggleCriterion = (id) => {
    setForm((f) => ({ ...f, criteria_ids: f.criteria_ids.includes(id) ? f.criteria_ids.filter((c) => c !== id) : [...f.criteria_ids, id] }));
  };

  const memberNames = (ids) => {
    return ids?.length ? `${ids.length} member${ids.length > 1 ? "s" : ""}` : "—";
  };

  return (
    <div>
      <PageHeader
        title="Training Session Log"
        subtitle="Daily & weekly record of every coaching, group and SOP session."
        action={
          <div className="flex items-center gap-3">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="session-type-filter" className="w-[180px] border-slate-700"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {SESSION_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="add-session-button" onClick={openNew} className="bg-[#C9A24B] text-[#0B1120] hover:bg-[#b8923f] gap-2">
              <Plus className="h-4 w-4" /> Log Session
            </Button>
          </div>
        }
      />

      <Card className="p-0 overflow-hidden">
        {sessions.length === 0 ? (
          <EmptyState icon={ClipboardList} title="No sessions logged" hint="Log your first training or coaching session." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead>Date</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Members</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Follow-up</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sessions.map((s) => (
                <TableRow key={s.id} data-testid={`session-row-${s.id}`} className="border-slate-800 hover:bg-slate-800/40">
                  <TableCell className="font-mono text-slate-300">{s.date}</TableCell>
                  <TableCell className="text-slate-300">{deptName(s.department_id)}</TableCell>
                  <TableCell><Badge tone="slate">{s.session_type}</Badge></TableCell>
                  <TableCell className="text-slate-400">{memberNames(s.member_ids)}</TableCell>
                  <TableCell className="font-mono text-slate-400">{s.duration}m</TableCell>
                  <TableCell>{s.follow_up_required ? <Badge tone="amber">Due {s.follow_up_date || "—"}</Badge> : <span className="text-slate-600 text-sm">—</span>}</TableCell>
                  <TableCell className="text-right">
                    <button onClick={() => openEdit(s)} className="text-slate-400 hover:text-[#C9A24B] mr-3" data-testid={`edit-session-${s.id}`}><Pencil className="h-4 w-4 inline" /></button>
                    <button onClick={() => remove(s.id)} className="text-slate-400 hover:text-[#DC2626]" data-testid={`delete-session-${s.id}`}><Trash2 className="h-4 w-4 inline" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F172A] border-slate-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Edit Session" : "Log Training Session"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Date</Label><Input data-testid="session-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="border-slate-700" /></div>
              <div><Label>Duration (min)</Label><Input data-testid="session-duration" type="number" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="border-slate-700" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Department</Label>
                <Select value={form.department_id} onValueChange={(v) => setForm({ ...form, department_id: v, member_ids: [], criteria_ids: [] })}>
                  <SelectTrigger data-testid="session-department" className="border-slate-700"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Session Type</Label>
                <Select value={form.session_type} onValueChange={(v) => setForm({ ...form, session_type: v })}>
                  <SelectTrigger data-testid="session-type" className="border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent>{SESSION_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {form.department_id && (
              <div>
                <Label>Team Members</Label>
                <div className="border border-slate-700 rounded-md p-3 max-h-32 overflow-y-auto space-y-2 mt-1">
                  {members.length === 0 && <p className="text-slate-500 text-sm">No members in this department.</p>}
                  {members.map((m) => (
                    <label key={m.id} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                      <Checkbox checked={form.member_ids.includes(m.id)} onCheckedChange={() => toggleMember(m.id)} data-testid={`session-member-${m.id}`} />
                      {m.name} <span className="text-slate-600 text-xs">({m.employee_id})</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {form.department_id && criteria.length > 0 && (
              <div>
                <Label>Topics / Criteria Covered</Label>
                <div className="border border-slate-700 rounded-md p-3 max-h-32 overflow-y-auto space-y-2 mt-1">
                  {criteria.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                      <Checkbox checked={form.criteria_ids.includes(c.id)} onCheckedChange={() => toggleCriterion(c.id)} />
                      {c.name}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div><Label>Outcome Notes</Label><Textarea value={form.outcome_notes} onChange={(e) => setForm({ ...form, outcome_notes: e.target.value })} className="border-slate-700" /></div>
            <div className="flex items-center justify-between">
              <Label className="cursor-pointer">Follow-up Required</Label>
              <Switch checked={form.follow_up_required} onCheckedChange={(v) => setForm({ ...form, follow_up_required: v })} data-testid="session-followup-switch" />
            </div>
            {form.follow_up_required && (
              <div><Label>Follow-up Date</Label><Input type="date" value={form.follow_up_date} onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })} className="border-slate-700" /></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-session-button" onClick={save} className="bg-[#C9A24B] text-[#0B1120] hover:bg-[#b8923f]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
